import { useMemo, useRef, useState } from "react";
import { Box, Flex, Input, Tag, TagCloseButton, TagLabel, Text } from "@chakra-ui/react";
import { User } from "@phosphor-icons/react";
import { prettyPrintCodeName } from "../../../../../utils/text-utils";
import { sortByLabel } from "../../../../../utils/userSort";

// Deterministic avatar colour from a name, so the same person always gets the same colour across
// renders/sessions without storing one. Small, fixed palette rather than a full colour wheel so
// every result stays legibly dark-on-white.
const PALETTE = ["#DD6B20", "#2B6CB0", "#4A8A0A", "#805AD5", "#2C7A7B", "#B83280", "#975A16"];
const colorFor = (name) => {
  const sum = [...(name || "")].reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return PALETTE[sum % PALETTE.length];
};
const initialsFor = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const displayName = (user) => [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email;

// No dedicated job-title field exists on the org user object — `roles` (an array of
// {id, role}) is the closest thing to the brief's "role", so the first one is humanized
// ("ORG_ADMIN" -> "Org Admin") as a stand-in. Falls back to userType, then nothing.
const roleLabelFor = (user) =>
  prettyPrintCodeName(user.roles?.[0]?.role) || prettyPrintCodeName(user.userType) || "";

/**
 * The Cc tag-input: type to search org users, pick from a dropdown, remove a chip with its ×.
 *
 * Hand-built rather than adapted from `chakra-react-select`/`FormMultiSelect` (see the design
 * brief's own avatar-initials + "name, then role · email" row, which neither of those render) —
 * plain Chakra primitives give full control over that row shape and over the specific keyboard
 * contract the brief asks for (↑/↓, Enter, Backspace-on-empty).
 *
 * `value` / `onChange` carry full user objects (not just ids) so the picker and the live preview
 * can both read `displayName`/`email` without a second lookup against `users`.
 */
export default function CcPeoplePicker({ value, onChange, users, excludeIds = [] }) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef(null);
  const blurTimeout = useRef(null);

  const selectedIds = useMemo(() => new Set(value.map((u) => u.id)), [value]);
  const excluded = useMemo(() => new Set(excludeIds), [excludeIds]);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sortByLabel(users, displayName).filter((u) => {
      if (selectedIds.has(u.id) || excluded.has(u.id)) return false;
      if (!q) return true;
      const haystack = `${displayName(u)} ${roleLabelFor(u)} ${u.email}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [users, query, selectedIds, excluded]);

  const addUser = (user) => {
    onChange([...value, user]);
    setQuery("");
    setHighlighted(0);
    inputRef.current?.focus();
  };

  const removeUser = (userId) => onChange(value.filter((u) => u.id !== userId));

  const openDropdown = () => {
    window.clearTimeout(blurTimeout.current);
    setIsOpen(true);
  };
  // A short delay rather than closing immediately on blur — otherwise a mousedown-then-click on a
  // dropdown row blurs the input first and the row unmounts before the click registers.
  const closeDropdownSoon = () => {
    blurTimeout.current = window.setTimeout(() => setIsOpen(false), 120);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      openDropdown();
      setHighlighted((i) => Math.min(i + 1, Math.max(suggestions.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (isOpen && suggestions[highlighted]) addUser(suggestions[highlighted]);
    } else if (e.key === "Backspace" && query === "" && value.length > 0) {
      removeUser(value[value.length - 1].id);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  return (
    <Box position="relative">
      <Flex
        wrap="wrap"
        align="center"
        gap={2}
        px={2}
        py={1.5}
        minH="44px"
        border="1px solid"
        borderColor={isOpen ? "lime" : "gray.200"}
        boxShadow={isOpen ? "0 0 0 3px rgba(133,194,42,.18)" : "none"}
        borderRadius="10px"
        bg="white"
        transition="all 0.1s"
      >
        {value.map((user) => (
          <Tag key={user.id} size="md" borderRadius="8px" bg="#FFF8E1" px={2} h="28px">
            <User size={14} color="#718096" />
            <TagLabel ml={1.5} fontSize="12px" fontWeight="700" letterSpacing="0.02em" color="gray.700" textTransform="uppercase">
              {user.email}
            </TagLabel>
            <TagCloseButton aria-label={`Remove ${displayName(user)}`} onClick={() => removeUser(user.id)} />
          </Tag>
        ))}
        <Input
          ref={inputRef}
          variant="unstyled"
          flex="1"
          minW="180px"
          fontSize="13.5px"
          placeholder="Add people from your organisation…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlighted(0);
            openDropdown();
          }}
          onFocus={openDropdown}
          onBlur={closeDropdownSoon}
          onKeyDown={onKeyDown}
        />
      </Flex>

      {isOpen && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          mt={1.5}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="10px"
          boxShadow="0 12px 32px rgba(5,31,35,.14)"
          maxH="250px"
          overflowY="auto"
          zIndex={5}
          p={1.5}
        >
          {suggestions.length === 0 ? (
            <Text px={2} py={2} fontSize="13px" color="gray.400">
              No matching people in your organisation
            </Text>
          ) : (
            suggestions.map((user, index) => (
              <Flex
                key={user.id}
                align="center"
                gap={2.5}
                px={2.5}
                py={2}
                borderRadius="6px"
                cursor="pointer"
                bg={index === highlighted ? "gray.50" : "transparent"}
                onMouseEnter={() => setHighlighted(index)}
                // mousedown, not click — fires before the input's onBlur closes the dropdown.
                onMouseDown={(e) => {
                  e.preventDefault();
                  addUser(user);
                }}
              >
                <Flex
                  w="28px"
                  h="28px"
                  borderRadius="full"
                  bg={colorFor(displayName(user))}
                  color="white"
                  fontSize="11px"
                  fontWeight="700"
                  align="center"
                  justify="center"
                  flexShrink={0}
                >
                  {initialsFor(displayName(user))}
                </Flex>
                <Box minW={0}>
                  <Text fontSize="13.5px" fontWeight="600" color="gray.800" noOfLines={1}>
                    {displayName(user)}
                  </Text>
                  <Text fontSize="12px" color="gray.400" noOfLines={1}>
                    {[roleLabelFor(user), user.email].filter(Boolean).join(" · ")}
                  </Text>
                </Box>
              </Flex>
            ))
          )}
        </Box>
      )}
    </Box>
  );
}
